import { useEffect } from 'react';

import { useStoreContext } from 'features/store';

import { AutoSelectClosestStorePrompt as AutoSelectClosestStorePromptControl } from 'experiments/location-modal-with-map/control/features/store/components/auto-select-closest-store-prompt/auto-select-closest-store-prompt';
import { AutoSelectClosestStorePrompt as AutoSelectClosestStorePromptTreatment } from 'experiments/location-modal-with-map/treatment/features/store/components/auto-select-closest-store-prompt/auto-select-closest-store-prompt';
import { useLocationModalWithMap } from 'experiments/location-modal-with-map/use-location-modal-with-map';
import { useRoute } from 'hooks/navigation/use-route';
import useAuthFlow from 'state/auth/hooks/use-auth-flow';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useDerivedServiceModeContext } from 'state/service-mode/derived';

import { useRecentOrderCooldown } from './hooks/use-recent-order-cooldown';
import { resetCloserStorePromptDismissOnAppOpen } from './session';
import { isEligibleRoute } from './utils';

/**
 * Mounts the closer-store prompt when the user is on an eligible route with a
 * selected pickup store, location permission, and feature flag enabled.
 * Cooldown after an interaction is enforced inside the prompt hook; a separate,
 * shorter cooldown after any recent order (`useRecentOrderCooldown`) is enforced
 * here, independent of the unrelated order-status-banner display timeout.
 */
export const AutoSelectClosestStorePromptWrapper = () => {
  const enableAutoSelectClosestStorePrompt = useFlag(
    LaunchDarklyFlag.ENABLE_AUTO_SELECT_CLOSEST_STORE_PROMPT
  );
  const isLocationModalWithMapEnabled = useLocationModalWithMap();
  const { pathname } = useRoute();
  const { store } = useStoreContext();
  const activeStoreId = store?._id;
  const { isPermissionGranted, userCoordinates, setPermissionChecking } = useGeolocation();
  const { isDelivery } = useDerivedServiceModeContext();
  const { isInAuthFlow } = useAuthFlow();
  const hasRecentOrderCooldown = useRecentOrderCooldown();

  resetCloserStorePromptDismissOnAppOpen();

  const isRouteEligible = isEligibleRoute(pathname);

  const isEligible =
    !hasRecentOrderCooldown &&
    !isInAuthFlow &&
    enableAutoSelectClosestStorePrompt &&
    !isDelivery &&
    isRouteEligible &&
    !!activeStoreId &&
    isPermissionGranted &&
    !!userCoordinates;

  const shouldTryEnableLocation =
    !isDelivery &&
    enableAutoSelectClosestStorePrompt &&
    !!activeStoreId &&
    !isPermissionGranted &&
    isRouteEligible;

  useEffect(() => {
    if (shouldTryEnableLocation) {
      setPermissionChecking();
    }
  }, [
    enableAutoSelectClosestStorePrompt,
    isPermissionGranted,
    setPermissionChecking,
    shouldTryEnableLocation,
    store,
  ]);

  if (!isEligible) {
    return null;
  }

  return isLocationModalWithMapEnabled ? (
    <AutoSelectClosestStorePromptTreatment
      activeStoreId={activeStoreId}
      userCoordinates={userCoordinates}
    />
  ) : (
    <AutoSelectClosestStorePromptControl
      activeStoreId={activeStoreId}
      userCoordinates={userCoordinates}
    />
  );
};
